import { memo, useEffect } from 'react';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { nodeTypeConfig } from '../../stores/flowBuilderStore';
import {
  Play,
  MessageSquare,
  Image,
  File,
  Grid,
  Layers,
  Edit,
  List,
  GitBranch,
  GitMerge,
  Shuffle,
  Globe,
  Clock,
  Sparkles,
  Brain,
  Heart,
  User,
  Users,
  XCircle,
  UserPlus,
  UserCheck,
  Tag,
  ArrowRight,
  Box,
  Key,
  Hash,
  Search,
  HelpCircle,
  EyeOff,
  Mic,
  Scan,
  Braces,
  Database,
  IdCard,
} from 'lucide-react';
import type { NodeType } from '../../types';

export const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  play: Play,
  key: Key,
  'message-square': MessageSquare,
  image: Image,
  file: File,
  grid: Grid,
  layers: Layers,
  edit: Edit,
  list: List,
  'git-branch': GitBranch,
  'git-merge': GitMerge,
  shuffle: Shuffle,
  variable: Hash,
  globe: Globe,
  clock: Clock,
  sparkles: Sparkles,
  brain: Brain,
  heart: Heart,
  user: User,
  users: Users,
  'x-circle': XCircle,
  'user-plus': UserPlus,
  'user-check': UserCheck,
  tag: Tag,
  'arrow-right': ArrowRight,
  box: Box,
  search: Search,
  'help-circle': HelpCircle,
  'eye-off': EyeOff,
  mic: Mic,
  scan: Scan,
  braces: Braces,
  database: Database,
  'id-card': IdCard,
};

interface CustomNodeData {
  type: NodeType;
  label: string;
  color: string;
  config: Record<string, unknown>;
}

const renderNodeContent = (nodeData: CustomNodeData) => {
  const config = nodeData.config as Record<string, any>;

  switch (nodeData.type) {
    case 'SEND_MESSAGE':
      return config.message ? (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate" title={config.message}>
          {config.message}
        </p>
      ) : null;

    case 'WHISPER_MESSAGE':
      return config.message ? (
        <p className="text-xs text-indigo-500 dark:text-indigo-300 truncate italic" title={config.message}>
          🤫 {config.message}
        </p>
      ) : null;

    case 'SEND_FILE':
      return (
        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded p-2">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 bg-blue-100 dark:bg-blue-500/20 rounded flex items-center justify-center">
              <File className="w-3 h-3 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-medium text-slate-700 dark:text-slate-200 truncate">
                {config.fileName || 'Arquivo'}
              </div>
              <div className="text-[9px] text-slate-500 dark:text-slate-400 truncate">
                {config.fileUrl || 'Sem URL'}
              </div>
            </div>
          </div>
          {config.caption && (
            <div className="text-[10px] text-slate-600 dark:text-slate-300 border-t border-slate-200 dark:border-slate-700 mt-1 pt-1 truncate">
              {config.caption}
            </div>
          )}
        </div>
      );

    case 'TRANSFER_TO_OPERATOR':
    case 'END_SESSION':
      return config.message ? (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate" title={config.message}>
          {config.message}
        </p>
      ) : null;

    case 'SEND_BUTTONS':
      return config.message ? (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate" title={config.message}>
          {config.message}
        </p>
      ) : null;

    case 'HTTP_REQUEST':
      return (
        <div className="text-xs text-slate-500 dark:text-slate-300 flex items-center gap-1">
          <span className="font-mono font-bold text-[10px] bg-slate-100 dark:bg-slate-700 px-1 rounded">
            {config.method || 'GET'}
          </span>
          <span className="truncate">{config.url || ''}</span>
        </div>
      );

    case 'CONDITION':
      return config.conditions?.length > 0 ? (
        <p className="text-xs text-slate-500 dark:text-slate-300">
          {config.conditions.length} condição(ões)
        </p>
      ) : null;

    case 'BUSINESS_HOURS':
      return (
        <div className="text-xs text-slate-600 dark:text-slate-300">
          <p className="text-slate-700 dark:text-slate-200">
            {config.startTime || '09:00'} - {config.endTime || '18:00'}
          </p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400">{(config.workDays || []).length} dias</p>
        </div>
      );

    case 'SET_VARIABLE':
      return config.variableName ? (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate">
          <span className="font-mono">{config.variableName}</span> = {config.value}
        </p>
      ) : null;

    case 'PARSE_JSON': {
      const source = String(config.sourceVariable || '').trim() || 'origem não definida';
      const output = String(config.outputVariable || 'parsed_json').trim() || 'parsed_json';
      return (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate" title={`${source} -> ${output}`}>
          Parse: {source}{' -> '}{output}
        </p>
      );
    }

    case 'SEARCH_CONTACT':
      return (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate">
          {config.searchIn === 'deal' ? 'Busca: Negócio' : 'Busca: Contato / Empresa'}
        </p>
      );

    case 'UPDATE_CONTACT': {
      const mode = String(config.mode || '').trim().toLowerCase();
      if (mode === 'get_crm_entity') {
        const target = config.targetEntity === 'company'
          ? 'Empresa'
          : config.targetEntity === 'contact'
            ? 'Contato'
            : 'Negócio';
        return (
          <p className="text-xs text-slate-500 dark:text-slate-300 truncate">
            Obter: {target}
          </p>
        );
      }
      if (mode === 'move_deal_stage') {
        const pipelineValue = String(config.pipelineId || 'current').trim();
        const pipelineLabel = pipelineValue === 'current'
          ? 'Pipeline atual'
          : pipelineValue === 'all'
            ? 'Todos'
            : `Pipeline ${pipelineValue}`;
        return (
          <p className="text-xs text-slate-500 dark:text-slate-300 truncate">
            Mover: {pipelineLabel}
          </p>
        );
      }

      const target = config.targetEntity === 'company'
        ? 'Empresa'
        : config.targetEntity === 'deal'
          ? 'Negócio'
          : 'Contato';
      const mappingMode = String(config.mappingMode || '').trim().toLowerCase() === 'json_auto'
        ? 'JSON auto'
        : target;
      return (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate">
          Editar: {mappingMode}
        </p>
      );
    }

    case 'MOVE_DEAL_STAGE': {
      const pipelineValue = String(config.pipelineId || 'current').trim();
      const pipelineLabel = pipelineValue === 'current'
        ? 'Pipeline atual'
        : pipelineValue === 'all'
          ? 'Todos'
          : `Pipeline ${pipelineValue}`;
      return (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate">
          Mover: {pipelineLabel}
        </p>
      );
    }

    case 'CREATE_COMPANY':
    case 'CREATE_CONTACT':
    case 'CREATE_DEAL': {
      const target = nodeData.type === 'CREATE_CONTACT'
        ? 'Contato'
        : nodeData.type === 'CREATE_DEAL'
          ? 'Negócio'
          : 'Empresa';
      const mappingMode = String(config.mappingMode || '').trim().toLowerCase() === 'json_auto'
        ? ' (JSON auto)'
        : '';
      return (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate">
          Criar: {target}{mappingMode}
        </p>
      );
    }

    case 'GET_CRM_ENTITY': {
      const target = config.targetEntity === 'company'
        ? 'Empresa'
        : config.targetEntity === 'contact'
          ? 'Contato'
          : 'Negócio';
      return (
        <p className="text-xs text-slate-500 dark:text-slate-300 truncate">
          Obter: {target}
        </p>
      );
    }

    default:
      return null;
  }
};

export function getOutputs(nodeData: any): { id?: string; label: string }[] {
  const c = (nodeData.config || {}) as any;
  switch (nodeData.type) {
    case 'CONDITION':
      return [
        ...(c.conditions || []).map((cond: any, i: number) => ({
          id: cond.id,
          label: String(cond?.name || `Caminho ${i + 1}`),
        })),
        { id: 'default', label: String(c?.defaultLabel || 'Padrão') },
      ];
    case 'WAIT_CHOICE':
      return (c.validChoices || []).map((choice: string, i: number) => ({
        id: `choice-${i}`,
        label: String(choice || `Opção ${i + 1}`),
      }));
    case 'SEND_BUTTONS': {
      const outs = (c.buttons || []).map((btn: any, i: number) => ({
        id: `button-${i}`,
        label: String(btn?.text || `Botão ${i + 1}`),
      }));
      if (c.fallbackEnabled) outs.push({ id: 'fallback', label: 'Outro' });
      return outs;
    }
    case 'MESSAGE_TYPE':
      return [
        { id: 'text', label: 'Texto' },
        { id: 'image', label: 'Imagem' },
        { id: 'audio', label: 'Áudio' },
        { id: 'default', label: 'Outro' },
      ];
    case 'SEARCH_CONTACT':
      if (c?.searchIn === 'deal') return [{ id: 'deal_found', label: 'Negócio' }, { id: 'not_found', label: 'Nada' }];
      if (c?.searchIn === 'lead') return [{ id: 'lead_found', label: 'Lead' }, { id: 'not_found', label: 'Nada' }];
      return [{ id: 'contact_found', label: 'Contato' }, { id: 'company_found', label: 'Empresa' }, { id: 'not_found', label: 'Nada' }];
    case 'EXTRACT_DATA':
      return [{ id: 'found', label: 'Encontrado' }, { id: 'not_found', label: 'Não encontrado' }];
    case 'BUSINESS_HOURS':
      return [{ id: 'open', label: 'Aberto' }, { id: 'closed', label: 'Fechado' }];
    case 'SWITCH':
      return [{ id: 'default', label: 'Padrão' }, { id: 'else', label: 'Senão' }];
    case 'END_SESSION':
      return [];
    default:
      return [{ label: '' }];
  }
}

function CustomNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as CustomNodeData;
  const updateNodeInternals = useUpdateNodeInternals();

  const resolvedTypeForIcon: NodeType = nodeData.type === 'UPDATE_CONTACT'
    ? (() => {
        const mode = String((nodeData.config as any)?.mode || '').trim().toLowerCase();
        if (mode === 'get_crm_entity') return 'GET_CRM_ENTITY';
        if (mode === 'move_deal_stage') return 'MOVE_DEAL_STAGE';
        return 'UPDATE_CONTACT';
      })()
    : nodeData.type;

  const Icon = iconMap[getIconName(resolvedTypeForIcon)] || MessageSquare;

  const getNodeWidth = () => {
    if (nodeData.type === 'MESSAGE_TYPE') return 260;
    return 244;
  };

  const nodeWidth = getNodeWidth();
  const config = (nodeData.config || {}) as Record<string, any>;
  const dynamicHandlesSignature = JSON.stringify({
    type: nodeData.type,
    searchIn: config.searchIn,
    conditionIds: Array.isArray(config.conditions)
      ? config.conditions.map((cond: any) => cond?.id || '').join('|')
      : '',
    waitChoiceCount: Array.isArray(config.validChoices) ? config.validChoices.length : 0,
    sendButtonsCount: Array.isArray(config.buttons) ? config.buttons.length : 0,
    fallbackEnabled: !!config.fallbackEnabled,
  });

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id));
    return () => window.cancelAnimationFrame(frame);
  }, [id, dynamicHandlesSignature, updateNodeInternals]);

  const typeLabel = nodeTypeConfig[nodeData.type as NodeType]?.label ?? nodeData.type;
  const bodyContent = renderNodeContent(nodeData);
  const outputs = getOutputs(nodeData);
  const isMultiOut = outputs.length > 1;

  return (
    <div
      className={`
        relative bg-slate-900 rounded-[10px] transition-all
        ${selected
          ? 'shadow-[0_0_0_1.5px_rgba(59, 130, 246,0.9),0_14px_36px_rgba(0,0,0,0.55)]'
          : 'border border-slate-700/70 shadow-[0_2px_12px_rgba(0,0,0,0.38)] hover:border-slate-600'}
      `}
      style={{ width: nodeWidth, minWidth: nodeWidth, maxWidth: nodeWidth }}
    >
      {nodeData.type !== 'START' && (
        <Handle
          type="target"
          position={Position.Left}
          style={{ top: 23, left: -6 }}
          className="!w-3 !h-3 !bg-slate-950 !border-2 !border-violet-500"
        />
      )}

      {/* header */}
      <div className="flex items-center gap-2.5 h-[46px] px-3">
        <span className="w-[26px] h-[26px] shrink-0 rounded-md flex items-center justify-center bg-slate-800 border border-white/5">
          <Icon className="w-4 h-4" style={{ color: nodeData.color }} />
        </span>
        <span className="flex flex-col min-w-0 flex-1 leading-tight">
          <span
            className="text-[9.5px] font-semibold uppercase tracking-[0.12em] truncate"
            style={{ color: nodeData.color }}
          >
            {typeLabel}
          </span>
          <span className="text-[14px] font-medium text-slate-100 truncate">
            {nodeData.label}
          </span>
        </span>
        <svg
          width="10"
          height="16"
          viewBox="0 0 10 16"
          fill="currentColor"
          className="text-slate-600 shrink-0"
          aria-hidden="true"
        >
          <circle cx="3" cy="4" r="1" /><circle cx="7" cy="4" r="1" />
          <circle cx="3" cy="8" r="1" /><circle cx="7" cy="8" r="1" />
          <circle cx="3" cy="12" r="1" /><circle cx="7" cy="12" r="1" />
        </svg>
      </div>

      {/* body — single-output nodes only */}
      {!isMultiOut && bodyContent && (
        <div className="px-3 pb-3 -mt-0.5 text-slate-300">{bodyContent}</div>
      )}

      {/* single output port (right, header-anchored) */}
      {!isMultiOut && outputs.length === 1 && (
        <Handle
          type="source"
          position={Position.Right}
          id={outputs[0].id}
          style={{ top: 23, right: -6 }}
          className="!w-3 !h-3 !bg-slate-950 !border-2 !border-violet-500"
        />
      )}

      {/* multi-output rows (right ports) */}
      {isMultiOut &&
        outputs.map((out, i) => (
          <div
            key={out.id ?? i}
            className="relative flex items-center h-8 pl-3 pr-5 border-t border-slate-700/60 text-[12px] text-slate-200"
          >
            <span className="text-slate-500 mr-1.5 shrink-0 text-[11px] leading-none">↳</span>
            <span className="truncate" title={out.label}>{out.label}</span>
            <Handle
              type="source"
              position={Position.Right}
              id={out.id}
              style={{ right: -6 }}
              className="!w-3 !h-3 !bg-slate-950 !border-2 !border-violet-500"
            />
          </div>
        ))}

    </div>
  );
}

export function getIconName(type: NodeType): string {
  const iconNames: Record<NodeType, string> = {
    START: 'play',
    KEYWORD_TRIGGER: 'key',
    SEND_MESSAGE: 'message-square',
    WHISPER_MESSAGE: 'eye-off',
    SEND_IMAGE: 'image',
    SEND_FILE: 'file',
    SEND_BUTTONS: 'help-circle',
    SEND_CAROUSEL: 'layers',
    WAIT_INPUT: 'edit',
    WAIT_CHOICE: 'list',
    CONDITION: 'git-branch',
    MESSAGE_TYPE: 'shuffle',
    BUSINESS_HOURS: 'clock',
    SWITCH: 'git-merge',
    SET_VARIABLE: 'variable',
    PARSE_JSON: 'braces',
    HTTP_REQUEST: 'globe',
    DELAY: 'clock',
    AI_RESPONSE: 'sparkles',
    AI_TRANSCRIBE_AUDIO: 'mic',
    AI_ANALYZE_IMAGE: 'scan',
    AI_INTENT: 'brain',
    AI_SENTIMENT: 'heart',
    TRANSFER_TO_OPERATOR: 'user',
    TRANSFER_TO_QUEUE: 'users',
    END_SESSION: 'x-circle',
    CREATE_LEAD: 'user-plus',
    CREATE_COMPANY: 'user-plus',
    CREATE_CONTACT: 'user-plus',
    CREATE_DEAL: 'user-plus',
    GET_CRM_ENTITY: 'database',
    UPDATE_CONTACT: 'user-check',
    MOVE_DEAL_STAGE: 'arrow-right',
    ADD_TAG: 'tag',
    SEARCH_CONTACT: 'search',
    GET_EMPLOYEE_DATA: 'id-card',
    EXTRACT_DATA: 'id-card',
    SUBFLOW: 'box',
  };
  return iconNames[type] || 'message-square';
}

export const CustomNode = memo(CustomNodeComponent);
